import choices from "choices.js";
import { FUZZY_OPTIONS } from "../utils/fuzzy-search";
import { createDescriptionTextNode } from "../utils/accessibility";
import { tabbableElements } from "../utils/selectors";
import { effectiveRangesStatus } from "../utils/effectiveRanges";

function selectBox($timeout, $logger) {
	return {
		restrict: "A",
		scope: { 
			label: "<", 
			search: "<", 
			single: "<", 
			setDisplay: "&", 
			setBox: "&", 
			simple: "<", 
			isReadonly: "<", 
			onFocus: "&", 
			onBlur: "&", 
			effectiveAsOf: "<",
			hasEffectiveRanges: "<",
			fuseOptions: "<",
			focusChoice: "<",
			renderChoiceLimit: "<",
			searchFields: "<",
			searchResultLimit: "<"
		},
		link: function(scope, element) {
			if(element[0].nodeName !== "SELECT") {
				$logger.error("select-box directive added to invalid element", { nodeName: element[0].nodeName });
			}
			else {
				const box = new choices(element[0], {
					itemSelectText: "",
					shouldSort: false,
					renderChoiceLimit: scope.renderChoiceLimit ?? 125,
					searchEnabled: scope.search,
					searchResultLimit: scope.searchResultLimit ?? 100,
					searchFields: scope.searchFields ?? ["customProperties.searchBy"],
					placeholder: false,
					removeItemButton: true,
					fuseOptions: scope.fuseOptions ?? FUZZY_OPTIONS.DATA,
					callbackOnCreateTemplates: scope.simple ? null : function(template) {
						const classNames = this.config.classNames;

						const getEffectiveClass = (data) => {
							if(!scope.effectiveAsOf || !scope.hasEffectiveRanges) {
								return "";
							}

							const ranges = data.customProperties?.effectiveRanges;
							if(!ranges?.length) {
								return "";
							}

							const status = effectiveRangesStatus(ranges, scope.effectiveAsOf);
							if(status.isValid)return "effective-valid";
							if(status.isInGrace)return "effective-grace";
							return "effective-invalid";
						};

						return {
							choice: (data) => {
								if(data.placeholder) {
									return template(
										`<div class="${classNames.item} ${classNames.itemChoice} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable} select-placeholder"
													${data.disabled ? 'aria-disabled="true"' : ""}
													role="option">
											${data.label}
										</div>`);
								}
								// aria-label="Pick ${scope.single ? "a" : ""} ${scope.label}"
								const effectiveClass = getEffectiveClass(data);

								const classes = {
									[classNames.item]: true,
									[classNames.itemChoice]: true,
									[classNames.highlightedState]: data.highlighted,
									[classNames.itemSelectable]: !data.highlighted,
									"deactivated-choice": data.customProperties?.deactivatedAt,
									"select-recent": data.customProperties?.isRecent,
									[effectiveClass]: !!effectiveClass
								};

								const classString = Object.entries(classes)
									.filter(([, value]) => value)
									.map(([key ]) => key)
									.join(" ");

								return template(
									`<div class="${classString}"
										data-choice
										data-select-choice
										data-choice-selectable
										data-id="${data.id}"
										id="${data.elementId}"
										data-value="${data.value}"
										${data.disabled ? 'aria-disabled="true"' : ""}
										role="option">
											${data.label}
									</div>
								`);
							},
							item: (data) => {
								let itemDisplay = scope?.setDisplay({ value: data.value }) ?? data.label;
								if((data.customProperties?.isActive !== undefined && !data.customProperties.isActive) || data.customProperties?.isRemoved) {
									itemDisplay = `<span class='deleted'>${itemDisplay} - Deleted</span>`;
								}

								const remove = this.config.removeItemButton ?
									'<button type="button" aria-hidden="true" role="none" tabindex="-1" class="choices__button" data-button="">Remove item</button>' :
									"";

								return template(`
									<div class="${classNames.item} ${data.highlighted ? 
											classNames.highlightedState : 
											classNames.itemSelectable} ${data.customProperties?.deactivatedAt ? "deactivated-choice" : ""}"
										data-item data-id="${data.id}" data-value="${data.value}"
										${data.disabled ? 'aria-disabled="true"' : ""}
									>
										${itemDisplay}${remove}
									</div>
								`);
							}
						};
					}
				});

				if(scope.isReadonly) {
					box.disable();
				}

				hookupSameChoiceChangePrevention(box);

				scope.setBox({ box });

				hookupPickOnTab(box);

				hookupMultiSelectInputBehavior(box);

				hookupReadOnlyBehavior(scope, box);

				hookupFocusAndBlur(scope, box);

				hookupDropdownChoiceFocus(scope, box);

				addAccessibilityHelpers(scope, box);

				scope.$on("$destroy", function() {
					box.containerOuter.onfocus = null;
					box.containerOuter.onblur = null;
				});
			}
		}
	};

	function hookupSameChoiceChangePrevention(box) {
		const originalSetValueByChoice = box.setValueByChoice.bind(box);

		box.setValueByChoice = function(choice) {
			const currentValue = box.getValue();
			if(choice == currentValue?.value) {
				return;
			}

			originalSetValueByChoice(choice);
		};
	}

	function hookupPickOnTab(box) {
		box.containerOuter.addEventListener("keydown", (e) => {
			if(e.key !== "Tab")
				return;

			if(!box.containerOuter.classList.contains("is-open"))
				return;

			const choice = box.choiceList.querySelector(".is-highlighted")?.dataset?.value;
			if(!choice)
				return;

			const current = box.getValue(true);
			if(current && current.length && current[0] == choice)
				return;

			e.preventDefault();
			e.stopImmediatePropagation();

			box.setValueByChoice(choice);
			box.passedElement.dispatchEvent(new Event("change"));
			box.hideDropdown();

			setTimeout(function() {
				const tabbables = tabbableElements();
				let index = tabbables.indexOf(box.containerOuter) + 1;
				if(index == tabbables.length)
					index = 0;
				tabbables[index].focus();
			}, 50);
		});
	}

	function hookupReadOnlyBehavior(scope, box) {
		scope.$watch(
			() => { return scope.isReadonly; },
			(newReadonly) => {
				if(!newReadonly) {
					box.enable();
				}
				else {
					box.disable();
				}
			}
		);
	}

	function hookupMultiSelectInputBehavior(box) {
		if(box.isSelectMultipleElement) {
			box.passedElement.addEventListener("change", () => {
				if(box.currentState.choices.length === 0) {
					box.input.classList.add("hidden");
				}
				else {
					box.input.classList.remove("hidden");
					box.input.style.width = "";
				}
			});
		}
	}

	function addAccessibilityHelpers(scope, box) {
		if(scope.label) {
			box.containerOuter.setAttribute("aria-label", scope.label);
			box.input.setAttribute(
				"aria-label",
				`Pick ${scope.single ? "a " : ""}${scope.label}`
			);
		}

		const helperText = createDescriptionTextNode("Press escape to clear field");
		box.containerOuter.appendChild(helperText);
		box.containerOuter.setAttribute("aria-describedby", helperText.id);
		box.containerOuter.addEventListener("keydown", (e) => {
			if(e.key !== "Escape" || box.containerOuter.classList.contains("is-open")) {
				return;
			}

			const value = box.getValue(true);
			const hasValue = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== "";
			if(hasValue) {
				box.removeActiveItems();
				box.passedElement.dispatchEvent(new Event("change"));
			}
		});
	}

	function hookupFocusAndBlur(scope, box) {
		const focus = function(event) {
			scope.onFocus({ "$event": event });
			$timeout(() => {
				scope.$apply();
			});
		};
		box.containerOuter.onfocus = focus;
		box.input.onfocus = focus;

		//apply blur methods
		const blur = function(event) {
			scope.onBlur({ "$event": event });
			box.clearInput();

			$timeout(() => {
				scope.$apply();
			});
		};
		box.containerOuter.onblur = blur;
		box.input.onblur = blur;
	}

	function hookupDropdownChoiceFocus(scope, box) {
		const focusChoice = function() {
			if(scope.focusChoice === undefined || scope.focusChoice === null) {
				return;
			}

			const choice = Array.from(box.choiceList.querySelectorAll("[data-choice-selectable]"))
				.find(candidate => candidate.dataset.value === `${scope.focusChoice}`);
			if(!choice) {
				return;
			}

			box._highlightChoice(choice);
			box.choiceList.scrollTop = Math.max(
				0,
				choice.offsetTop - ((box.choiceList.clientHeight - choice.offsetHeight) / 2)
			);
		};

		box.passedElement.addEventListener("showDropdown", focusChoice);
		scope.$on("$destroy", function() {
			box.passedElement.removeEventListener("showDropdown", focusChoice);
		});
	}
}
export default angular
	.module("vesl.behaviors.select-box", [])
	.directive("selectBox", selectBox).name;
